import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import '@theme/product-form';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #shippingProtectionEnabled = true;

  #shippingProtectionObserver;

  #recommendationObserver;

  #shippingProtectionSyncQueued = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('click', this.#handleAnalyticsClick);
    this.#shippingProtectionObserver = new MutationObserver(this.#queueShippingProtectionSync);
    this.#shippingProtectionObserver.observe(this, { childList: true, subtree: true });
    this.#queueShippingProtectionSync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('click', this.#handleAnalyticsClick);
    this.#shippingProtectionObserver?.disconnect();
    this.#recommendationObserver?.disconnect();
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.open();
    }
  };

  #expandMobileOrderSummary() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const orderSummary = this.querySelector('[data-sdc-order-summary]');
    if (orderSummary instanceof HTMLDetailsElement) orderSummary.open = true;
  }

  #cartAnalyticsContext() {
    return {
      surface: 'cart_drawer',
      itemCount: Number(this.dataset.cartItemCount) || 0,
      cartTotal: Number(this.dataset.cartTotal) || 0,
      currency: this.dataset.cartCurrency || '',
    };
  }

  #track(eventName, payload, options) {
    window.StrikemanAnalytics?.track?.(
      eventName,
      Object.assign(this.#cartAnalyticsContext(), payload || {}),
      options
    );
  }

  #handleAnalyticsClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const checkoutButton = target.closest('.cart__checkout-button, #nvd-with-checkout-btn, #nvd-without-checkout-btn');
    if (checkoutButton) {
      this.#track('cart_drawer_checkout_clicked', {
        shippingProtectionEnabled: this.#shippingProtectionEnabled,
      });
      return;
    }

    const summaryToggle = target.closest('.sdc-summary-disclosure > summary');
    if (summaryToggle) {
      window.setTimeout(() => {
        const details = summaryToggle.closest('details');
        this.#track('cart_drawer_order_summary_toggled', {
          expanded: Boolean(details?.open),
        });
      });
    }

    const recommendation = target.closest('.sdc-recommendation');
    if (!recommendation) return;

    const recommendationAction = target.closest('.sdc-recommendation__quick-add')
      ? target.closest('button')
        ? 'quick_add'
        : 'choose_options'
      : 'product_link';

    this.#track('cart_drawer_recommendation_clicked', {
      action: recommendationAction,
      productId: recommendation.dataset.productId || '',
      productHandle: recommendation.dataset.productHandle || '',
      variantId: recommendation.dataset.variantId || '',
      recommendationPosition: Number(recommendation.dataset.recommendationPosition) || 0,
    });
  };

  #observeRecommendations() {
    this.#recommendationObserver?.disconnect();
    const section = this.querySelector('[data-sdc-recommendations]');
    if (!section) return;

    const recommendations = Array.from(section.querySelectorAll('.sdc-recommendation')).map((item) => ({
      productId: item.dataset.productId || '',
      productHandle: item.dataset.productHandle || '',
      variantId: item.dataset.variantId || '',
      recommendationPosition: Number(item.dataset.recommendationPosition) || 0,
    }));
    const signature = recommendations.map((item) => item.productHandle).join('|') || 'resources';

    if (!('IntersectionObserver' in window)) {
      this.#track('cart_drawer_recommendations_viewed', { recommendations }, { onceKey: `cart-recommendations:${signature}` });
      return;
    }

    this.#recommendationObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
        this.#track(
          recommendations.length ? 'cart_drawer_recommendations_viewed' : 'cart_drawer_resources_viewed',
          { recommendations },
          { onceKey: `cart-recommendations:${signature}` }
        );
        observer.disconnect();
      },
      { threshold: [0.5] }
    );
    this.#recommendationObserver.observe(section);
  }

  #queueShippingProtectionSync = () => {
    if (this.#shippingProtectionSyncQueued) return;

    this.#shippingProtectionSyncQueued = true;
    requestAnimationFrame(() => {
      this.#shippingProtectionSyncQueued = false;
      this.#syncShippingProtection();
    });
  };

  #toggleShippingProtection = () => {
    this.#shippingProtectionEnabled = !this.#shippingProtectionEnabled;
    this.#syncShippingProtection();
    this.#track('cart_drawer_shipping_protection_toggled', {
      enabled: this.#shippingProtectionEnabled,
    });
  };

  #syncShippingProtection() {
    const widget = this.querySelector('#nvd-standard-widget-container');
    const controls = this.querySelector('[data-sdc-shipping-protection-controls]');
    const summaryRow = this.querySelector('[data-sdc-shipping-protection-summary]');
    if (!widget || !controls || !summaryRow) return;

    widget.dataset.sdcProtectionEnabled = String(this.#shippingProtectionEnabled);

    let toggle = controls.querySelector('.sdc-shipping-protection-toggle');
    if (!toggle) {
      toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'sdc-shipping-protection-toggle';
      toggle.setAttribute('role', 'checkbox');
      toggle.innerHTML =
        '<span class="sdc-shipping-protection-toggle__box" aria-hidden="true"></span><span>Shipping Protection</span>';
      toggle.addEventListener('click', this.#toggleShippingProtection);
      controls.prepend(toggle);
    }
    toggle.setAttribute('aria-checked', String(this.#shippingProtectionEnabled));

    const info = widget.querySelector('.nvd-secondary-content');
    if (info) {
      controls.querySelectorAll('.nvd-secondary-content').forEach((existingInfo) => {
        if (existingInfo !== info) existingInfo.remove();
      });
      if (info.parentElement !== controls) controls.append(info);
    }

    const protectionAmount = widget.querySelector('.nvd-title span')?.textContent.trim() || '$0.00';
    const summaryAmount = summaryRow.querySelector('[data-sdc-shipping-protection-amount]');
    const displayedProtectionAmount = this.#shippingProtectionEnabled ? protectionAmount : '$0.00';
    if (summaryAmount && summaryAmount.textContent.trim() !== displayedProtectionAmount) {
      summaryAmount.textContent = displayedProtectionAmount;
    }
    summaryRow.hidden = false;

    const cartTotal = this.querySelector('[data-cart-subtotal]');
    const compactTotal = this.querySelector('.sdc-summary-disclosure__compact-total strong');
    const protectedCheckout = widget.querySelector('#nvd-with-checkout-btn');
    const unprotectedCheckout = widget.querySelector('#nvd-without-checkout-btn');
    if (!cartTotal || !protectedCheckout || !unprotectedCheckout) return;

    const rawBaseTotal =
      cartTotal.dataset.sdcBaseTotal || cartTotal.getAttribute('value') || cartTotal.textContent.trim();
    const baseTotal = rawBaseTotal.replace(/\s+[A-Z]{3}\s*$/, '');
    cartTotal.dataset.sdcBaseTotal = baseTotal;
    if (compactTotal && !compactTotal.dataset.sdcBaseTotal) {
      compactTotal.dataset.sdcBaseTotal = compactTotal.textContent.trim().replace(/\s+[A-Z]{3}\s*$/, '');
    }

    const protectedMoney = protectedCheckout.textContent.match(/[$][\d,.]+/)?.[0];
    const protectedTotal = protectedMoney || baseTotal;
    const displayedTotal = this.#shippingProtectionEnabled ? protectedTotal : baseTotal;

    if (cartTotal.textContent.trim() !== displayedTotal) cartTotal.textContent = displayedTotal;
    if (compactTotal && compactTotal.textContent.trim() !== displayedTotal) compactTotal.textContent = displayedTotal;

    const unprotectedLabel = unprotectedCheckout.querySelector('span') || unprotectedCheckout;
    const checkoutLabel = `Checkout · ${baseTotal}`;
    if (unprotectedLabel.textContent.trim() !== checkoutLabel) unprotectedLabel.textContent = checkoutLabel;
  }

  open() {
    this.#expandMobileOrderSummary();
    this.showDialog();
    this.#queueShippingProtectionSync();
    this.#track('cart_drawer_opened');
    window.setTimeout(() => this.#observeRecommendations());

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.#track('cart_drawer_closed');
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
